/**
 * Configuration options for the unique selector generator
 */
export interface SelectorOptions {
  /** Whether to use element ID in selector generation */
  useId?: boolean;
  /** Whether to use data attributes in selector generation */
  useDataAttributes?: boolean;
  /** Whether to use CSS classes in selector generation */
  useClasses?: boolean;
  /** Maximum depth to traverse up the DOM tree */
  maxDepth?: number;
  /** Array of class names or regex patterns to exclude */
  excludeClasses?: (string | RegExp)[];
  /** Whether to prefer stable attributes over dynamic ones */
  preferStableAttributes?: boolean;
  /** Whether to prefer shorter selectors over longer ones */
  preferShortSelectors?: boolean;
}

/**
 * CSS specificity breakdown
 */
export interface SpecificityBreakdown {
  ids: number;
  classes: number;
  attributes: number;
  elements: number;
}

/**
 * CSS specificity information
 */
export interface Specificity {
  score: number;
  breakdown: SpecificityBreakdown;
}

/**
 * Advanced selector result with metadata
 */
export interface AdvancedSelectorResult {
  selector: string;
  specificity: Specificity;
  isStable: boolean;
  performance: number;
}

/**
 * Type guard to check if element is a valid DOM Element
 */
function isValidElement(element: unknown): element is Element {
  return element instanceof Element && element.nodeType === Node.ELEMENT_NODE;
}

/**
 * Type guard to check if pattern is RegExp
 */
function isRegExp(pattern: string | RegExp): pattern is RegExp {
  return pattern instanceof RegExp;
}

/**
 * Safe CSS escape that handles edge cases
 */
function safeCSSEscape(value: string): string {
  if (!value || typeof value !== 'string') return '';

  try {
    if (typeof CSS !== 'undefined' && CSS.escape) {
      return CSS.escape(value);
    }
    return value.replace(/[!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~]/g, '\\$&');
  } catch {
    return '';
  }
}

/**
 * Safely gets attribute value with null checking
 */
function getSafeAttributeValue(
  element: Element,
  attributeName: string
): string {
  try {
    return element.getAttribute(attributeName) || '';
  } catch {
    return '';
  }
}

/**
 * Gets count of elements matching a selector (for finding best selector)
 */
function getBestSelectorCount(selector: string): number {
  try {
    return document.querySelectorAll(selector).length;
  } catch {
    return 9999; // Invalid selector gets worst score
  }
}

/**
 * Builds incremental path by adding parents until unique
 */
function buildIncrementalPath(
  element: Element,
  options: SelectorOptions
): string {
  const selectors: string[] = [];
  let current: Element | null = element;
  const seenElements = new Set<Element>();

  // Build selectors from target up to root
  while (
    current &&
    current !== current.ownerDocument?.documentElement &&
    !seenElements.has(current)
  ) {
    seenElements.add(current);

    const isTargetElement = current === element;
    const elementSelector = generateElementSelector(
      current,
      options,
      true,
      isTargetElement
    );

    if (elementSelector) {
      selectors.unshift(elementSelector);

      // Test current path
      const currentPath = selectors.join(' > ');
      if (isUniqueSelector(currentPath, element)) {
        return currentPath;
      }
    }

    current = current.parentElement;
  }

  return selectors.join(' > ');
}

/**
 * Generates a unique CSS selector for any DOM element
 * FIXED: Now uses incremental strategy testing like DevTools
 */
export function getUniqueSelector(
  element: Element,
  options: SelectorOptions = {}
): string {
  // Validate input
  if (!isValidElement(element)) {
    throw new Error('Invalid element provided - must be a DOM Element');
  }

  if (!element.ownerDocument || !element.ownerDocument.contains(element)) {
    throw new Error('Element must be attached to the DOM');
  }

  // Strategy 1: Try element selector alone
  const elementSelector = generateElementSelector(element, options, true, true);
  if (isUniqueSelector(elementSelector, element)) {
    return elementSelector;
  }

  let bestSelector = elementSelector;
  let bestCount = getBestSelectorCount(elementSelector);

  // Strategy 2: Try with parent IDs (most efficient)
  let parent = element.parentElement;
  while (parent && parent !== parent.ownerDocument?.documentElement) {
    if (parent.id) {
      const escapedId = safeCSSEscape(parent.id);
      if (escapedId) {
        const parentIdSelector = `#${escapedId} > ${elementSelector}`;
        if (isUniqueSelector(parentIdSelector, element)) {
          return parentIdSelector;
        }

        // Track best selector
        const count = getBestSelectorCount(parentIdSelector);
        if (count > 0 && count < bestCount) {
          bestSelector = parentIdSelector;
          bestCount = count;
        }
      }
    }
    parent = parent.parentElement;
  }

  // Strategy 3: Build incremental path with parent elements
  const fullPath = buildIncrementalPath(element, options);
  if (isUniqueSelector(fullPath, element)) {
    return fullPath;
  }

  // Return best selector found
  return bestSelector;
}

/**
 * Generates selector for a single element with sibling disambiguation
 * FIXED: Now always adds nth-child when siblings exist (like DevTools)
 */
function generateElementSelector(
  element: Element,
  options: SelectorOptions,
  useSiblingDisambiguation: boolean = true,
  isTargetElement: boolean = false
): string {
  const { excludeClasses = [], useClasses = true } = options;

  // Handle special cases
  if (element.tagName === 'BODY') {
    return 'body';
  }

  if (element.tagName === 'HTML') {
    return 'html';
  }

  let selector = element.tagName.toLowerCase();

  // Add ID if available and unique
  if (element.id) {
    const escapedId = safeCSSEscape(element.id);
    if (escapedId) {
      const idSelector = `#${escapedId}`;
      // If this ID is unique, we can just return it (unless it's the target and we want classes too)
      if (!isTargetElement && isUniqueSelector(idSelector, element)) {
        return idSelector;
      }
      selector = idSelector; // Use ID instead of tag for better specificity
    }
  }

  // Add stable classes - be more generous for target element
  if (useClasses) {
    const classNames = getSafeAttributeValue(element, 'class');

    if (classNames) {
      const stableClasses = classNames
        .split(/\s+/)
        .filter((cls): cls is string => Boolean(cls?.trim()))
        .filter((cls) => !isExcludedClass(cls, excludeClasses))
        .filter((cls) => !isDynamicClass(cls))
        .slice(0, isTargetElement ? 5 : 2); // More classes for target

      if (stableClasses.length > 0) {
        const escapedClasses = stableClasses
          .map((cls) => safeCSSEscape(cls))
          .filter(Boolean);

        if (escapedClasses.length > 0) {
          // If we already have an ID, add classes to it
          if (element.id) {
            selector += escapedClasses.map((cls) => `.${cls}`).join('');
          } else {
            selector += escapedClasses.map((cls) => `.${cls}`).join('');
          }
        }
      }
    }
  }

  // ALWAYS add nth-child if there are multiple siblings - key insight from the simple script!
  if (useSiblingDisambiguation && element.parentElement) {
    const siblings = Array.from(element.parentElement.children);
    if (siblings.length > 1) {
      const nthChild = siblings.indexOf(element) + 1;
      selector += `:nth-child(${nthChild})`;
    }
  }

  return selector;
}

/**
 * Checks if a selector uniquely identifies the target element
 */
function isUniqueSelector(selector: string, targetElement: Element): boolean {
  if (!selector || !isValidElement(targetElement)) return false;

  try {
    const document = targetElement.ownerDocument;
    if (!document) return false;

    const elements = document.querySelectorAll(selector);
    return elements.length === 1 && elements[0] === targetElement;
  } catch {
    return false;
  }
}

/**
 * Checks if a class name should be excluded based on the exclusion patterns
 */
function isExcludedClass(
  className: string,
  excludeClasses: (string | RegExp)[]
): boolean {
  if (!className || typeof className !== 'string') return true;

  try {
    return excludeClasses.some((pattern) => {
      if (isRegExp(pattern)) {
        try {
          return pattern.test(className);
        } catch {
          return false;
        }
      }
      return pattern === className;
    });
  } catch {
    return false;
  }
}

/**
 * Detects if a class name is likely dynamic
 */
function isDynamicClass(className: string): boolean {
  if (!className || typeof className !== 'string') return true;

  try {
    const dynamicPatterns: readonly RegExp[] = [
      /^css-\w+$/, // CSS-in-JS generated classes
      /^\w+-\d{6,}$/, // Hash-based classes
      /^_\w+$/, // Underscore prefixed (CSS modules)
      /^[a-f0-9]{8,}$/, // Long hex strings
      /^.+\d{13,}$/, // Timestamp-based classes
    ] as const;

    return dynamicPatterns.some((pattern) => {
      try {
        return pattern.test(className);
      } catch {
        return false;
      }
    });
  } catch {
    return true;
  }
}

/**
 * Calculates CSS specificity score
 */
function calculateSpecificity(selector: string): Specificity {
  if (!selector || typeof selector !== 'string') {
    return {
      score: 0,
      breakdown: { ids: 0, classes: 0, attributes: 0, elements: 0 },
    };
  }

  try {
    const ids = (selector.match(/#[^\s+>~.[:]+/g) || []).length;
    const classes = (selector.match(/\.[^\s+>~.[:]+/g) || []).length;
    const attributes = (selector.match(/\[[^\]]+\]/g) || []).length;
    const pseudoClasses = (
      selector.match(/:[^\s+>~.[:]+(?:\([^)]*\))?/g) || []
    ).filter(
      (pseudo) => !pseudo.match(/^::(before|after|first-line|first-letter)$/)
    ).length;
    const elements = (
      selector.match(/(?:^|[\s+>~])([a-zA-Z][\w-]*|\\.)(?=[\s+>~.[:#]|$)/g) ||
      []
    ).length;

    const totalClasses = classes + attributes + pseudoClasses;

    return {
      score: ids * 100 + totalClasses * 10 + elements,
      breakdown: {
        ids,
        classes: totalClasses,
        attributes,
        elements,
      },
    };
  } catch {
    return {
      score: 0,
      breakdown: { ids: 0, classes: 0, attributes: 0, elements: 0 },
    };
  }
}

/**
 * Checks if selector contains potentially unstable elements
 */
function containsDynamicElements(selector: string): boolean {
  if (!selector || typeof selector !== 'string') return true;

  try {
    const unstablePatterns = [
      /nth-child/,
      /nth-of-type/,
      /nth-last-child/,
      /nth-last-of-type/,
      /first-child/,
      /last-child/,
      /only-child/,
    ];

    return unstablePatterns.some((pattern) => pattern.test(selector));
  } catch {
    return true;
  }
}

/**
 * Measures selector performance
 */
function measureSelectorPerformance(selector: string): number {
  if (!selector || typeof selector !== 'string') return -1;

  try {
    const start = performance.now();
    const document = globalThis.document;

    if (!document) return -1;

    document.querySelectorAll(selector);
    return performance.now() - start;
  } catch {
    return -1;
  }
}

/**
 * Enhanced version with additional utilities and type safety
 */
export function getAdvancedSelector(
  element: Element,
  options: SelectorOptions = {}
): AdvancedSelectorResult {
  if (!isValidElement(element)) {
    throw new Error('Invalid element provided - must be a DOM Element');
  }

  try {
    const selector = getUniqueSelector(element, options);

    return {
      selector,
      specificity: calculateSpecificity(selector),
      isStable: !containsDynamicElements(selector),
      performance: measureSelectorPerformance(selector),
    };
  } catch {
    const fallbackSelector = element.tagName.toLowerCase();
    return {
      selector: fallbackSelector,
      specificity: calculateSpecificity(fallbackSelector),
      isStable: true,
      performance: -1,
    };
  }
}

/**
 * Utility type for framework-specific configurations
 */
export interface FrameworkPresets {
  tailwind: SelectorOptions;
  bootstrap: SelectorOptions;
  materialUI: SelectorOptions;
  styledComponents: SelectorOptions;
}

/**
 * Pre-configured options for popular CSS frameworks
 */
export const frameworkPresets: FrameworkPresets = {
  tailwind: {
    excludeClasses: [
      /^hover:/,
      /^focus:/,
      /^active:/,
      /^disabled:/,
      /^group-hover:/,
      /^group-focus:/,
      /^transition-/,
      /^duration-/,
      /^ease-/,
      /^animate-/,
      /^transform/,
      /^translate-/,
      /^rotate-/,
      /^scale-/,
    ],
    preferStableAttributes: false,
    preferShortSelectors: true,
    maxDepth: 10,
  },
  bootstrap: {
    excludeClasses: [
      'active',
      'show',
      'fade',
      'collapsing',
      'disabled',
      /^btn-outline-/,
      /^text-/,
      /^bg-/,
    ],
    preferStableAttributes: false,
    preferShortSelectors: true,
  },
  materialUI: {
    excludeClasses: [/^Mui\w+-/, /^css-/, /^makeStyles-/, /^jss\d+/, /^r\d+/],
    preferStableAttributes: false,
    preferShortSelectors: true,
  },
  styledComponents: {
    excludeClasses: [/^sc-/, /^css-/, /^styled__/, /^[a-zA-Z]+-\w{5,}/],
    useDataAttributes: false,
    preferStableAttributes: false,
    preferShortSelectors: true,
  },
};

/**
 * Convenience function for framework-specific selector generation
 */
export function getFrameworkSelector<T extends keyof FrameworkPresets>(
  element: Element,
  framework: T,
  additionalOptions: Partial<SelectorOptions> = {}
): string {
  if (!isValidElement(element)) {
    throw new Error('Invalid element provided - must be a DOM Element');
  }

  if (!(framework in frameworkPresets)) {
    throw new Error(`Unknown framework: ${framework}`);
  }

  try {
    const preset = frameworkPresets[framework];
    const mergedOptions: SelectorOptions = { ...preset, ...additionalOptions };

    return getUniqueSelector(element, mergedOptions);
  } catch {
    return element.tagName.toLowerCase();
  }
}

// Re-export main function for backwards compatibility
export { getUniqueSelector as default };
